// Material 3 Chip (https://m3.material.io/components/chips): one 32dp height, small (8dp) shape, label-large text.
//   variant 'outlined' (default) | 'elevated'; type 'assist' | 'filter' | 'input' | 'suggestion'
//   filter chips: selected = secondary-container with a leading check; unselected = outlined
//   tone (optional, for static status/role labels): 'primary' | 'secondary' | 'tertiary' | 'error' | 'success'
//     -> role container fill + on-container text, no outline
//   mono: monospace label (ports, paths, hex codes)
// Every color comes from --m3-* tokens; do not pass sx overrides for color/height/shape.
(function () {
  const { Chip: MuiChip } = MaterialUI;

  const TONES = {
    primary: ['var(--m3-primary-container)', 'var(--m3-on-primary-container)'],
    secondary: ['var(--m3-secondary-container)', 'var(--m3-on-secondary-container)'],
    tertiary: ['var(--m3-tertiary-container)', 'var(--m3-on-tertiary-container)'],
    error: ['var(--m3-error-container)', 'var(--m3-on-error-container)'],
    success: ['var(--m3-success-container)', 'var(--m3-on-success-container)']
  };
  const motion = 'var(--m3-duration-short) var(--m3-easing-standard)';
  const layer = (fg, pct) => `color-mix(in srgb, ${fg} ${pct}, transparent)`;

  function Chip({
    variant = 'outlined', // 'outlined' | 'elevated'
    type = 'assist',      // 'assist' | 'filter' | 'input' | 'suggestion'
    tone,
    mono = false,
    label,
    icon,
    leadingIcon,
    selected = false,
    onClick,
    onDelete,
    disabled = false,
    size, // accepted for compatibility; M3 chips have a single 32dp size
    color, // legacy MUI prop, ignored
    sx = {},
    ...props
  }) {
    const { GoogleIcon } = window.Components || {};
    const isFilter = type === 'filter';
    const interactive = !!onClick;

    let bg = 'transparent';
    let fg = 'var(--m3-on-surface-variant)';
    let border = '1px solid var(--m3-outline-variant)';
    let shadow = 'none';
    let iconColor = 'var(--m3-primary)';
    let stateFg = 'var(--m3-on-surface-variant)';

    if (variant === 'elevated') {
      bg = 'var(--m3-surface-container-low)';
      border = '1px solid transparent';
      shadow = 'var(--m3-elevation-1)';
    }
    if (tone && TONES[tone]) {
      [bg, fg] = TONES[tone];
      border = '1px solid transparent';
      iconColor = fg;
      stateFg = fg;
    }
    if (isFilter && selected) {
      bg = 'var(--m3-secondary-container)';
      fg = 'var(--m3-on-secondary-container)';
      border = '1px solid transparent';
      iconColor = fg;
      stateFg = fg;
    }

    const chipIcon = leadingIcon || icon || (isFilter && selected && GoogleIcon ? <GoogleIcon name="check" size={18} /> : undefined);

    return (
      <MuiChip
        label={label}
        icon={chipIcon}
        onClick={onClick}
        onDelete={onDelete}
        disabled={disabled}
        variant="filled"
        sx={{
          height: 32,
          maxWidth: '100%',
          borderRadius: 'var(--m3-shape-small)',
          backgroundColor: bg,
          color: fg,
          border,
          boxShadow: shadow,
          fontSize: '0.875rem',
          lineHeight: '20px',
          fontWeight: 500,
          letterSpacing: '0.1px',
          fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : 'inherit',
          transition: `background-color ${motion}, box-shadow ${motion}`,
          // Touch screens: 44dp tall tap area for chips that can be pressed
          ...(onClick && { '@media (pointer: coarse)': { '&::after': { content: '""', position: 'absolute', inset: '-6px 0' } } }),
          '& .MuiChip-label': { px: 2, overflow: 'hidden', textOverflow: 'ellipsis' },
          '& .MuiChip-icon': { ml: 1, mr: -0.75, color: `${iconColor} !important`, fontSize: '18px !important' },
          '& .MuiChip-deleteIcon': { color: fg, fontSize: 18, mr: 1 },
          ...(interactive && {
            cursor: 'pointer',
            '&:hover': { backgroundColor: `color-mix(in srgb, ${stateFg} 8%, ${bg === 'transparent' ? 'transparent' : bg})` },
            '&:focus-visible': { outline: '3px solid var(--m3-secondary)', outlineOffset: 2, backgroundColor: layer(stateFg, '12%') },
            '&:active': { backgroundColor: layer(stateFg, '12%') }
          }),
          '&.Mui-disabled': { opacity: 0.38 },
          ...sx
        }}
        {...props}
      />
    );
  }

  const AssistChip = (props) => <Chip type="assist" {...props} />;
  const FilterChip = (props) => <Chip type="filter" {...props} />;
  const InputChip = (props) => <Chip type="input" {...props} />;
  const SuggestionChip = (props) => <Chip type="suggestion" {...props} />;

  window.Components = window.Components || {};
  window.Components.Chip = Chip;
  window.Components.capitalize = s => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : '');
  window.Components.AssistChip = AssistChip;
  window.Components.FilterChip = FilterChip;
  window.Components.InputChip = InputChip;
  window.Components.SuggestionChip = SuggestionChip;
})();
